import { supabase } from '../lib/supabase';
import { Database } from '../types/database.types';
import { AvailabilitySlot } from '../types/models';

type AvailabilitySlotRow = Database['public']['Tables']['availability_slots']['Row'];
type ConsultationBookingInsert = Database['public']['Tables']['consultation_bookings']['Insert'];

// Convert database row to application model
const toAvailabilitySlot = (row: AvailabilitySlotRow): AvailabilitySlot => ({
  id: row.id,
  slotDate: row.slot_date,
  slotTime: row.slot_time,
  meetingType: row.meeting_type,
  isAvailable: row.is_available,
  maxBookings: row.max_bookings,
  currentBookings: row.current_bookings,
  durationMinutes: row.duration_minutes,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const bookingService = {
  // Get all available slots for client booking
  async getAvailableSlots(): Promise<{ data: AvailabilitySlot[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('availability_slots')
        .select('*')
        .eq('is_available', true)
        .gte('slot_date', new Date().toISOString().split('T')[0]) // Only future dates
        .order('slot_date', { ascending: true })
        .order('slot_time', { ascending: true });

      if (error) throw error;
      return { data: data?.map(toAvailabilitySlot) ?? null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Create a booking request (public access)
  async createBooking(booking: {
    clientEmail: string;
    clientName: string;
    clientPhone?: string;
    slotId: string;
    notes?: string;
    location?: string; // Add location parameter
  }): Promise<{ data: any | null; error: Error | null }> {
    try {
      // First, upsert client profile
      const { data: clientData, error: clientError } = await supabase.rpc(
        'upsert_client_by_email',
        {
          p_email: booking.clientEmail,
          p_full_name: booking.clientName,
          p_phone: booking.clientPhone || null,
        }
      );

      if (clientError) throw clientError;

      const clientId = clientData;

      // Create booking with location
      const insertData: ConsultationBookingInsert = {
        client_id: clientId,
        slot_id: booking.slotId,
        meeting_type: 'standard',
        booking_status: 'requested',
        notes: booking.notes || null,
        location: booking.location || 'Online', // Include location with default
      };

      const { data, error } = await supabase
        .from('consultation_bookings')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};